"use client";

import { useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Pin, PinOff } from "lucide-react";
import { createNote, updateNote } from "@/app/actions";
import type { NoteColor } from "@/lib/colors";
import { noteColorClass } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ColorPicker } from "@/components/notes/color-picker";

export function Composer() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [color, setColor] = useState<NoteColor>("default");
  const [pinned, setPinned] = useState(false);
  const [pending, startTransition] = useTransition();
  const rootRef = useRef<HTMLDivElement>(null);

  function reset() {
    setTitle("");
    setContent("");
    setColor("default");
    setPinned(false);
    setOpen(false);
  }

  function save() {
    if (!title.trim() && !content.trim()) {
      reset();
      return;
    }
    startTransition(async () => {
      const res = await createNote({ title, content, color });
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      if (pinned) await updateNote({ id: res.data.id, pinned: true });
      reset();
      router.refresh();
      toast.success("Nota creada");
    });
  }

  return (
    <div
      ref={rootRef}
      onBlur={(e) => {
        if (open && !pending && !rootRef.current?.contains(e.relatedTarget as Node)) save();
      }}
      className={cn(
        "mx-auto w-full max-w-xl rounded-2xl ring-1 ring-foreground/10 shadow-sm transition-all",
        noteColorClass(color),
      )}
    >
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center px-5 py-3.5 text-left text-muted-foreground"
        >
          <span className="font-display text-base">Escribe una nota…</span>
        </button>
      ) : (
        <div className="flex flex-col gap-2 p-3">
          <div className="flex items-start gap-2">
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título"
              className="h-9 border-0 bg-transparent px-2 font-display text-base font-medium shadow-none focus-visible:ring-0 dark:bg-transparent"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={pinned ? "Quitar fijado" : "Fijar nota"}
              title={pinned ? "Quitar fijado" : "Fijar"}
              onClick={() => setPinned((p) => !p)}
              className={cn(pinned && "text-primary")}
            >
              {pinned ? <Pin className="fill-current" /> : <PinOff />}
            </Button>
          </div>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Toma una nota…"
            rows={3}
            className="min-h-20 resize-none border-0 bg-transparent px-2 shadow-none focus-visible:ring-0 dark:bg-transparent"
          />
          <div className="flex items-center justify-between px-1">
            <ColorPicker value={color} onChange={setColor} align="start" />
            <div className="flex items-center gap-1.5">
              <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={pending}>
                Cerrar
              </Button>
              <Button type="button" size="sm" onClick={save} disabled={pending}>
                {pending ? "Guardando…" : "Crear nota"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
